import { Box, Button, Stack, Tab, Tabs, Grid } from "@mui/material";
import {
  ModalProps,
  createUseModal,
  useFormModal,
} from "@imminently/react/context/modals";
import {
  Dialog,
  DialogActions,
  DialogContent,
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import {
  FormInputText,
  FormToggleGroup,
  FormInputSelect,
} from "@imminently/react/ui/forms";
import { yupResolver } from "@hookform/resolvers/yup";
import lodash from "lodash";
import React, { useState } from "react";
import * as yup from "yup";

import { scrollable } from "@/components/scrollable";
import {
  obsMetricToLabel,
  urinalysisOptions,
} from "@/resources/observations_metricUtils";
import { Observation, PatientEncounter } from "@/resources/types";
import { encounterService } from "@/services";
import { TabPanel } from "./addObservation";
import { modalStyles as styles } from "./styles";

const allowEmpty = (schema: any) => {
  return yup.lazy((value: any) => (value === "" ? yup.string() : schema));
};

const schema = yup.object().shape({
  urinalysis: yup.object().shape({
    abnormalities: yup.string(),
    urineSent: yup.string(),
    pregnancy: yup.string(),
    glucose: allowEmpty(yup.string()),
    protein: allowEmpty(yup.string()),
    blood: allowEmpty(yup.string()),
    bilirubin: allowEmpty(yup.string()),
    nitrite: allowEmpty(yup.string()),
    urobilinogen: allowEmpty(yup.string()),
    leukocytes: allowEmpty(yup.string()),
    ph: allowEmpty(yup.string()),
    ketone: allowEmpty(yup.string()),
    specificGravity: allowEmpty(yup.string()),
  }).optional(),
  note: yup.string(),
});

const initialState = {
  urinalysis: {
    abnormalities: "",
    urineSent: "",
    pregnancy: "",
    glucose: "",
    protein: "",
    blood: "",
    bilirubin: "",
    nitrite: "",
    urobilinogen: "",
    leukocytes: "",
    ph: "",
    ketone: "",
    specificGravity: "",
  },
  note: "",
};

const FormInlineButtons = (props: {
  label: string;
  name: string;
  control: any;
  options: any;
}) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Text bold>{props.label}</Text>
    <FormToggleGroup
      control={props.control}
      name={props.name}
      exclusive
      options={props.options}
    />
  </Stack>
);

const FormDropSelect = (props: {
  label: string;
  disabled: boolean;
  name: string;
  control: any;
  options: any;
  placeholder: string;
}) => (
  <Stack gap={1}>
    <Text bold>{props.label}</Text>
    <FormInputSelect
      fullWidth
      disabled={props.disabled}
      control={props.control}
      name={props.name}
      options={props.options}
      label={props.placeholder}
    />
  </Stack>
);

type AddModalProps = { encounter: PatientEncounter; onSuccess: () => void };

const AddUrinalysisModal = ({ props }: ModalProps<AddModalProps>) => {
  const { encounter } = props;
  const { useCreate } = encounterService.useSubresource(
    "observations",
    encounter.id
  );
  const { mutateAsync, isLoading } = useCreate();

  const { methods, notify, close } = useFormModal({
    mode: "all",
    // Cast to any: yupResolver currently has an incompatible inferred type and TS don't like
    resolver: yupResolver(schema) as any,
    defaultValues: initialState,
  });
  const {
    control,
    formState: { isValid },
    handleSubmit,
    watch,
  } = methods;

  /* eslint-disable complexity */
  const handleAdd = async (data: any) => {
    const { note, urinalysis } = data;

    const newObservation = {
      note: note === "" ? undefined : note,
      urinalysis: {
        distream: urinalysis.urineSent,
        pregnancy: urinalysis.pregnancy,
      } as any,
    };

    if (urinalysis.abnormalities !== "") {
      newObservation.urinalysis.abnormalities = urinalysis.abnormalities;

      if (urinalysis.abnormalities === "Yes") {
        newObservation.urinalysis = {
          ...newObservation.urinalysis,
          glucose: urinalysis.glucose || undefined,
          protein: urinalysis.protein || undefined,
          blood: urinalysis.blood || undefined,
          bilirubin: urinalysis.bilirubin || undefined,
          nitrite: urinalysis.nitrite || undefined,
          urobilinogen: urinalysis.urobilinogen || undefined,
          leukocytes: urinalysis.leukocytes || undefined,
          ph: urinalysis.ph || undefined,
          ketone: urinalysis.ketone || undefined,
          specificGravity: urinalysis.specificGravity || undefined,
        };
      }
    }

    // Remove empty strings
    const cleanedObservation = {
      ...newObservation,
      urinalysis: lodash.omitBy(newObservation.urinalysis, (x) => x === ""),
    };

    try {
      await mutateAsync(cleanedObservation as Partial<Observation>);
    } catch (e: any) {
      notify.error(e.message ?? "Failed to add observation.");
      return;
    }

    close(true);
    props.onSuccess?.();
  };

  const [selectedTab, setSelectedTab] = useState(0);
  const abnormalitiesValue = watch("urinalysis.abnormalities");
  const noAbnormalities = ["", "No"].includes(abnormalitiesValue ?? "");
  const tabItems = ["Urine", "Pregnancy"];
  const commonUrinalysisProps = { control, disabled: noAbnormalities };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <Dialog open onClose={() => close()} maxWidth="lg">
      <DialogContent sx={{ width: "56.25rem", p: 0, overflow: "overlay" }}>
        <Grid container spacing={0}>
          <Grid item xs={12} sm={4} md={4} sx={{ bgcolor: "#C3C8DE" }}>
            <Box css={styles.container_left}>
              <Text variant="h3">Add urinalysis</Text>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                orientation="vertical"
                variant="scrollable"
                css={styles.tabs}
              >
                {tabItems.map((item, index) => (
                  <Tab
                    css={[
                      styles.tab,
                      selectedTab === index ? styles.active_tab : null,
                    ]}
                    key={item}
                    label={
                      <>
                        <Text
                          css={[
                            styles.circle,
                            selectedTab === index ? styles.active_circle : null,
                          ]}
                        >
                          {index + 1}
                        </Text>
                        <Text variant="h5" css={styles.tab_title}>
                          {item}
                        </Text>
                      </>
                    }
                    {...(selectedTab === index && { selected: true })}
                  />
                ))}
              </Tabs>
              <Box css={styles.notes_block}>
                <FormInputText
                  fullWidth
                  control={control}
                  size="medium"
                  name="note"
                  label="Observation notes..."
                  multiline
                  rows={7}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={8} md={8}>
            <Box sx={styles.formArea} className={scrollable}>
              <TabPanel value={selectedTab} index={0}>
                <Stack gap={0.5}>
                  <Stack gap={2} marginBottom={1}>
                    <FormInlineButtons
                      label="Abnormalities detected"
                      name="urinalysis.abnormalities"
                      control={control}
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                    />

                    <FormInlineButtons
                      label="Midstream urine specimen sent"
                      name="urinalysis.urineSent"
                      control={control}
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                    />
                  </Stack>
                  <Stack
                    css={
                      noAbnormalities
                        ? { filter: "grayscale(1) contrast(0.1) opacity(0.5)" }
                        : {}
                    }
                  >
                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisLeukocytes")}
                      placeholder="Select leukocytes level"
                      name="urinalysis.leukocytes"
                      options={urinalysisOptions.leukocytes}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisNitrite")}
                      placeholder="Select nitrite level"
                      name="urinalysis.nitrite"
                      options={urinalysisOptions.nitrite}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisUrobilinogen")}
                      placeholder="Select urobilinogen level"
                      name="urinalysis.urobilinogen"
                      options={urinalysisOptions.urobilinogen}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisProtein")}
                      placeholder="Select protein level"
                      name="urinalysis.protein"
                      options={urinalysisOptions.protein}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisPh")}
                      placeholder="Select pH level"
                      name="urinalysis.ph"
                      options={urinalysisOptions.ph}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisBlood")}
                      placeholder="Select blood level"
                      name="urinalysis.blood"
                      options={urinalysisOptions.blood}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisSpecificGravity")}
                      placeholder="Select specific gravity"
                      name="urinalysis.specificGravity"
                      options={urinalysisOptions.specificGravity}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisKetone")}
                      placeholder="Select ketone level"
                      name="urinalysis.ketone"
                      options={urinalysisOptions.ketone}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisBilirubin")}
                      placeholder="Select bilirubin level"
                      name="urinalysis.bilirubin"
                      options={urinalysisOptions.bilirubin}
                    />

                    <FormDropSelect
                      {...commonUrinalysisProps}
                      label={obsMetricToLabel("urinalysisGlucose")}
                      placeholder="Select glucose level"
                      name="urinalysis.glucose"
                      options={urinalysisOptions.glucose}
                    />
                  </Stack>
                </Stack>
              </TabPanel>

              <TabPanel value={selectedTab} index={1}>
                <Stack gap={1}>
                  <Text bold>{obsMetricToLabel("urinalysisPregnancy")}</Text>
                  <FormToggleGroup
                    fullWidth
                    control={control}
                    name="urinalysis.pregnancy"
                    exclusive
                    options={[
                      { value: "Negative", label: "Negative" },
                      { value: "Positive", label: "Positive" },
                    ]}
                  />
                </Stack>
              </TabPanel>
            </Box>
            <DialogActions>
              <Grid container>
                <Grid item xs={6}>
                  {selectedTab > 0 && (
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedTab(selectedTab - 1)}
                    >
                      Previous
                    </Button>
                  )}
                  {selectedTab < tabItems.length - 1 && (
                    <Button
                      variant="outlined"
                      disabled={!isValid || isLoading}
                      onClick={() => setSelectedTab(selectedTab + 1)}
                    >
                      Next - {tabItems[selectedTab + 1]}
                    </Button>
                  )}
                </Grid>
                <Grid item xs={6} style={{ textAlign: "right" }}>
                  <Button
                    disabled={!isValid || isLoading}
                    onClick={handleSubmit(handleAdd)}
                  >
                    Submit urinalysis
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export const useAddUrinalysisModal = createUseModal(AddUrinalysisModal);
